import React, { useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  FolderTree,
  LoaderCircle,
  Plus,
  Search,
  Settings2,
  SlidersHorizontal,
  Tags,
  X,
} from 'lucide-react';
import { useMasterData } from '../hooks/useMasterData';
import { CreateServiceProviderTypeModal } from './CreateServiceProviderTypeModal';
import { EditServiceProviderTypeModal } from './EditServiceProviderTypeModal';
import { CreateCategoryModal } from './CreateCategoryModal';
import { CreateServiceProviderTypeRequest, CreateCategoryRequest, ServiceProviderType, UpdateServiceProviderTypeRequest } from '../models/ServiceProviderType';
import { formatDateString } from '../lib/utils';

const filterControlClassName = 'block h-11 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100';

const ServicesListCreator: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'service-types' | 'categories'>('service-types');
  const [areTypeFiltersOpen, setAreTypeFiltersOpen] = useState(false);
  const [areCategoryFiltersOpen, setAreCategoryFiltersOpen] = useState(false);
  const canDelete = (() => {
    try {
      const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      return currentUser.role === 'admin';
    } catch {
      return false;
    }
  })();
  const { 
    serviceProviderTypes, 
    categories, 
    loading, 
    error, 
    selectedType, 
    setSelectedType,
    manageMasterData
  } = useMasterData();

  // Search and pagination states for service provider types
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [typeCurrentPage, setTypeCurrentPage] = useState(1);
  const [typeItemsPerPage, setTypeItemsPerPage] = useState(10);

  // Search and pagination states for categories
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [categoryCurrentPage, setCategoryCurrentPage] = useState(1);
  const [categoryItemsPerPage, setCategoryItemsPerPage] = useState(10);

  // Modal states
  const [createTypeModalOpen, setCreateTypeModalOpen] = useState(false);
  const [createCategoryModalOpen, setCreateCategoryModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<ServiceProviderType | null>(null);
  const [isUpdatingType, setIsUpdatingType] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);



  // Filter service provider types based on search term
  const filteredServiceTypes = serviceProviderTypes.filter(type =>
    type.name.toLowerCase().includes(typeSearchTerm.toLowerCase()) ||
    type.description.toLowerCase().includes(typeSearchTerm.toLowerCase())
  );

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(categorySearchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(categorySearchTerm.toLowerCase())
  );

  // Pagination for service provider types
  const typeStartIndex = (typeCurrentPage - 1) * typeItemsPerPage;
  const typeEndIndex = typeStartIndex + typeItemsPerPage;
  const paginatedServiceTypes = filteredServiceTypes.slice(typeStartIndex, typeEndIndex);
  const typeTotalPages = Math.ceil(filteredServiceTypes.length / typeItemsPerPage);

  // Pagination for categories
  const categoryStartIndex = (categoryCurrentPage - 1) * categoryItemsPerPage;
  const categoryEndIndex = categoryStartIndex + categoryItemsPerPage;
  const paginatedCategories = filteredCategories.slice(categoryStartIndex, categoryEndIndex);
  const categoryTotalPages = Math.ceil(filteredCategories.length / categoryItemsPerPage);
  const typeActiveFilterCount = typeSearchTerm.trim() ? 1 : 0;
  const categoryActiveFilterCount = categorySearchTerm.trim() ? 1 : 0;

  // Handler functions
  const handleCreateServiceProviderType = async (data: CreateServiceProviderTypeRequest) => {
    try {
      const response = await manageMasterData({
        action: 'create',
        data: {
          serviceProviderTypes: [data]
        }
      });
      
      if (response.success) {
        setSuccessMessage(`Service provider type "${data.name}" created successfully!`);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error creating service provider type:', error);
      throw error;
    }
  };

  const handleCreateCategory = async (data: CreateCategoryRequest) => {
    try {
      // Validate required data
      if (!data.serviceProviderTypeTag) {
        throw new Error('Service provider type is required to create a category');
      }
      
      if (!data.tag || !data.name || !data.description || !data.icon) {
        throw new Error('All fields are required to create a category');
      }

      // Category creation logging removed for security
      
      const response = await manageMasterData({
        action: 'create',
        data: {
          categories: [data]
        }
      });
      
      if (response.success) {
        setSuccessMessage(`Category "${data.name}" created successfully!`);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  };

  const handleUpdateServiceProviderType = async (data: UpdateServiceProviderTypeRequest) => {
    try {
      setIsUpdatingType(true);
      const response = await manageMasterData({
        action: 'update',
        data: { serviceProviderTypes: [data] }
      });

      if (response.success) {
        setSuccessMessage(`Service provider type "${data.name || data.tag}" updated successfully!`);
        setTimeout(() => setSuccessMessage(null), 5000);
        setEditingType(null);
      }
    } catch (error) {
      console.error('Error updating service provider type:', error);
      throw error;
    } finally {
      setIsUpdatingType(false);
    }
  };

  const handleDeleteServiceProviderType = async (tag: string) => {
    if (!canDelete) return;

    if (!window.confirm(`Are you sure you want to delete this service provider type? This will also delete all its categories.`)) {
      return;
    }

    try {
      const response = await manageMasterData({
        action: 'delete',
        data: {
          serviceProviderTypeTags: [tag]
        }
      });
      
      if (response.success) {
        setSuccessMessage('Service provider type deleted successfully!');
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error deleting service provider type:', error);
    }
  };

  const handleDeleteCategory = async (category: any) => {
    if (!canDelete) return;

    if (!window.confirm(`Are you sure you want to delete this category?`)) {
      return;
    }

    try {
      // Category deletion logging removed for security
      
      const response = await manageMasterData({
        action: 'delete',
        data: {
          categoryTags: [category.tag]
        }
      });
      
      if (response.success) {
        setSuccessMessage('Category deleted successfully!');
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><FolderTree className="h-5 w-5" aria-hidden="true" /></span>
        <div><h2 className="text-lg font-semibold text-slate-950 sm:text-xl">Services master data</h2><p className="mt-1 text-sm text-slate-500">Manage service-provider types and their category hierarchy.</p></div>
      </div>
      
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 shadow-sm" role="status">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" /><p className="text-sm font-medium">{successMessage}</p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm" role="alert">
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" /><div><p className="text-sm font-semibold text-red-900">Unable to load master data</p><p className="mt-1 text-sm text-red-700">{error}</p></div>
        </div>
      )}
      
      {/* Tab Navigation */}
      <div className="mb-6 rounded-xl bg-slate-100 p-1">
          <nav className="grid grid-cols-1 gap-1 sm:grid-cols-2" aria-label="Master data views">
            <button
              type="button"
              onClick={() => setActiveTab('service-types')}
              className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                activeTab === 'service-types'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-slate-600 hover:bg-white/70 hover:text-slate-900'
              }`}
            >
              Service provider types <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs">{serviceProviderTypes.length}</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('categories')}
              className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                activeTab === 'categories'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-slate-600 hover:bg-white/70 hover:text-slate-900'
              }`}
              disabled={!selectedType}
            >
              Categories <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs">{categories.length}</span>
            </button>
          </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'service-types' && (
        <div>
          {/* Service Provider Types Data Table */}
          <section className="admin-table-shell">
            <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><Tags className="h-[18px] w-[18px]" aria-hidden="true" /></span><div><h3 className="text-base font-semibold text-slate-950">Service provider types</h3><p className="mt-0.5 text-xs text-slate-500" aria-live="polite">Showing {filteredServiceTypes.length === 0 ? 0 : typeStartIndex + 1}–{Math.min(typeEndIndex, filteredServiceTypes.length)} of {filteredServiceTypes.length} types</p></div></div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button type="button" onClick={() => setCreateTypeModalOpen(true)} className="inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"><Plus className="mr-2 h-4 w-4" aria-hidden="true" />Create type</button>
                <button type="button" onClick={() => setAreTypeFiltersOpen(current => !current)} aria-expanded={areTypeFiltersOpen} aria-controls="service-type-filters" className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areTypeFiltersOpen || typeActiveFilterCount > 0 ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}><SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />Filters{typeActiveFilterCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">{typeActiveFilterCount}</span>}<ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areTypeFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" /></button>
              </div>
            </div>
            <div id="service-type-filters" hidden={!areTypeFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex-1"><label htmlFor="service-type-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search service types</label><div className="relative"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" /><input id="service-type-search" type="search" placeholder="Name or description" className={`${filterControlClassName} pl-10`} value={typeSearchTerm} onChange={(event) => { setTypeSearchTerm(event.target.value); setTypeCurrentPage(1); }} /></div></div>
                <div className="sm:w-40"><label htmlFor="typeItemsPerPage" className="mb-1.5 block text-xs font-semibold text-slate-600">Rows</label><select id="typeItemsPerPage" className={filterControlClassName} value={typeItemsPerPage} onChange={(event) => { setTypeItemsPerPage(Number(event.target.value)); setTypeCurrentPage(1); }}><option value={5}>5 per page</option><option value={10}>10 per page</option></select></div>
              </div>
              {typeActiveFilterCount > 0 && <div className="mt-4 flex justify-end"><button type="button" onClick={() => { setTypeSearchTerm(''); setTypeCurrentPage(1); }} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"><X className="mr-2 h-4 w-4" aria-hidden="true" />Clear filters</button></div>}
            </div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12"><LoaderCircle className="mb-3 h-7 w-7 animate-spin text-blue-600" aria-hidden="true" /><p className="text-sm font-semibold text-slate-900">Loading service provider types…</p></div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Categories
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mobile Display
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedServiceTypes.map((type) => {
                        return (
                          <tr key={type.tag} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{type.name}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-500 max-w-xs truncate" title={type.description}>
                                {type.description.length > 20 ? `${type.description.substring(0, 20)}...` : type.description}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {type.categoryCount} categories
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              <div>{type.startingPrice == null ? 'No starting price' : `${type.currency || 'USD'} ${type.startingPrice.toFixed(2)}`}</div>
                              <div className="max-w-[180px] truncate text-xs text-gray-400" title={type.imageUrl || ''}>{type.imageUrl ? 'Image configured' : 'No image'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDateString(type.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => setEditingType(type)}
                                  className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 transition-colors"
                                  title="Edit Service Provider Type"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedType(type.tag);
                                    setActiveTab('categories');
                                  }}
                                  className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                                  title="View Categories"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                                {canDelete && <button
                                  onClick={() => handleDeleteServiceProviderType(type.tag)}
                                  className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                  title="Delete Service Provider Type"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-4">
                  {paginatedServiceTypes.map((type) => (
                    <div key={type.tag} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{type.name}</h3>
                          <p className="text-xs text-gray-500 mt-1">{formatDateString(type.createdAt)}</p>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => setEditingType(type)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 transition-colors"
                            title="Edit Service Provider Type"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedType(type.tag);
                              setActiveTab('categories');
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                            title="View Categories"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {canDelete && <button
                            onClick={() => handleDeleteServiceProviderType(type.tag)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                            title="Delete Service Provider Type"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3" title={type.description}>
                        {type.description.length > 20 ? `${type.description.substring(0, 20)}...` : type.description}
                      </p>
                      <p className="mb-3 text-xs text-gray-500">
                        {type.startingPrice == null ? 'No starting price' : `${type.currency || 'USD'} ${type.startingPrice.toFixed(2)}`} · {type.imageUrl ? 'Image configured' : 'No image'}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {type.categoryCount} categories
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {/* Service Provider Types Pagination */}
            {typeTotalPages > 1 && (
              <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    Page {typeCurrentPage} of {typeTotalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTypeCurrentPage(Math.max(1, typeCurrentPage - 1))}
                      disabled={typeCurrentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setTypeCurrentPage(Math.min(typeTotalPages, typeCurrentPage + 1))}
                      disabled={typeCurrentPage === typeTotalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'categories' && (
        <div>
          {!selectedType ? (
            <div className="text-center py-8">
              <div className="text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No Service Provider Type Selected</h3>
                <p className="mt-1 text-sm text-gray-500">Please select a service provider type to view its categories.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Categories Header */}
              <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <h3 className="text-sm font-semibold text-blue-900">
                  Categories for: {serviceProviderTypes.find(t => t.tag === selectedType)?.name}
                </h3>
                <p className="mt-1 text-xs text-blue-700">
                  Tag: <span className="rounded bg-blue-100 px-2 py-1 font-mono">{selectedType}</span>
                </p>
              </div>

              {/* Categories Data Table */}
              <section className="admin-table-shell">
                <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><FolderTree className="h-[18px] w-[18px]" aria-hidden="true" /></span><div><h3 className="text-base font-semibold text-slate-950">Service categories</h3><p className="mt-0.5 text-xs text-slate-500" aria-live="polite">Showing {filteredCategories.length === 0 ? 0 : categoryStartIndex + 1}–{Math.min(categoryEndIndex, filteredCategories.length)} of {filteredCategories.length} categories</p></div></div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button type="button" onClick={() => setCreateCategoryModalOpen(true)} disabled={!selectedType} className="inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"><Plus className="mr-2 h-4 w-4" aria-hidden="true" />Add category</button>
                    <button type="button" onClick={() => setAreCategoryFiltersOpen(current => !current)} aria-expanded={areCategoryFiltersOpen} aria-controls="category-filters" className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areCategoryFiltersOpen || categoryActiveFilterCount > 0 ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}><SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />Filters{categoryActiveFilterCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">{categoryActiveFilterCount}</span>}<ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areCategoryFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" /></button>
                  </div>
                </div>
                <div id="category-filters" hidden={!areCategoryFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="flex-1"><label htmlFor="category-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search categories</label><div className="relative"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" /><input id="category-search" type="search" placeholder="Name or description" className={`${filterControlClassName} pl-10`} value={categorySearchTerm} onChange={(event) => { setCategorySearchTerm(event.target.value); setCategoryCurrentPage(1); }} /></div></div>
                    <div className="sm:w-40"><label htmlFor="categoryItemsPerPage" className="mb-1.5 block text-xs font-semibold text-slate-600">Rows</label><select id="categoryItemsPerPage" className={filterControlClassName} value={categoryItemsPerPage} onChange={(event) => { setCategoryItemsPerPage(Number(event.target.value)); setCategoryCurrentPage(1); }}><option value={5}>5 per page</option><option value={10}>10 per page</option></select></div>
                  </div>
                  {categoryActiveFilterCount > 0 && <div className="mt-4 flex justify-end"><button type="button" onClick={() => { setCategorySearchTerm(''); setCategoryCurrentPage(1); }} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"><X className="mr-2 h-4 w-4" aria-hidden="true" />Clear filters</button></div>}
                </div>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12"><LoaderCircle className="mb-3 h-7 w-7 animate-spin text-blue-600" aria-hidden="true" /><p className="text-sm font-semibold text-slate-900">Loading categories…</p></div>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Category Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Description
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Icon
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Sort Order
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedCategories.map((category) => {
                            return (
                              <tr key={category.tag} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{category.name}</div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-500 max-w-xs truncate" title={category.description}>
                                    {category.description.length > 20 ? `${category.description.substring(0, 20)}...` : category.description}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {category.icon}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {category.sortOrder}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  {canDelete && <button
                                    onClick={() => handleDeleteCategory(category)}
                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                    title="Delete Category"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="lg:hidden space-y-4">
                      {paginatedCategories.map((category) => (
                        <div key={category.tag} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-gray-900">{category.name}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {category.icon}
                                </span>
                                <span className="text-xs text-gray-500">Order: {category.sortOrder}</span>
                              </div>
                            </div>
                            <div className="flex space-x-2 ml-4">
                              {canDelete && <button
                                onClick={() => handleDeleteCategory(category)}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Delete Category"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600" title={category.description}>
                            {category.description.length > 20 ? `${category.description.substring(0, 20)}...` : category.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                
                {/* Categories Pagination */}
                {categoryTotalPages > 1 && (
                  <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="text-sm text-gray-700">
                        Page {categoryCurrentPage} of {categoryTotalPages}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCategoryCurrentPage(Math.max(1, categoryCurrentPage - 1))}
                          disabled={categoryCurrentPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCategoryCurrentPage(Math.min(categoryTotalPages, categoryCurrentPage + 1))}
                          disabled={categoryCurrentPage === categoryTotalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}

      {/* Modals */}
      <CreateServiceProviderTypeModal
        isOpen={createTypeModalOpen}
        onClose={() => setCreateTypeModalOpen(false)}
        onSubmit={handleCreateServiceProviderType}
        loading={loading}
      />

      <CreateCategoryModal
        isOpen={createCategoryModalOpen}
        onClose={() => setCreateCategoryModalOpen(false)}
        onSubmit={handleCreateCategory}
        serviceProviderTypeTag={selectedType || ''}
        serviceProviderTypeName={serviceProviderTypes.find(t => t.tag === selectedType)?.name || ''}
        loading={loading}
      />

      <EditServiceProviderTypeModal
        isOpen={editingType !== null}
        serviceType={editingType}
        onClose={() => setEditingType(null)}
        onSubmit={handleUpdateServiceProviderType}
        loading={isUpdatingType}
      />

    </div>
  );
};

const Settings: React.FC = () => {
  const [activeSubMenu, setActiveSubMenu] = useState<string>('master-data');

  const subMenuItems = [
    { id: 'master-data', label: 'Master Data' },
  ];

  const renderSubMenuContent = () => {
    switch (activeSubMenu) {
      case 'master-data':
        return <ServicesListCreator />;
      default:
        return (
          <div className="p-8">
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Settings</h3>
              <p className="text-gray-600">Select a sub-menu option to configure settings.</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-2xl bg-slate-950 px-5 py-6 text-white shadow-sm sm:px-7 sm:py-7" aria-labelledby="settings-title">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_center,_rgba(59,130,246,0.22),_transparent_68%)]" />
        <div className="relative">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-blue-200"><Settings2 className="h-3.5 w-3.5" aria-hidden="true" />Configuration</div>
          <h1 id="settings-title" className="text-2xl font-semibold tracking-tight sm:text-3xl">Settings</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Manage the master data that powers service-provider types and categories.</p>
        </div>
      </section>

      <div className="flex flex-col gap-6 lg:flex-row">
          {/* Left Menu */}
          <aside className="w-full flex-shrink-0 lg:w-72">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:sticky lg:top-6">
              <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Settings menu</p>
              <nav className="space-y-2" aria-label="Settings sections">
                {subMenuItems.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => setActiveSubMenu(item.id)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                      activeSubMenu === item.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <FolderTree className="h-4 w-4" aria-hidden="true" />
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              {renderSubMenuContent()}
            </div>
          </div>
      </div>
    </div>
  );
};

export { Settings };
